import { useRef } from "react";
import { api } from "../lib/api";

// A save that can be retried without writing twice.
//
// The store refuses a replayed request when it carries the same key, but only the client can
// supply one that survives a retry: a key minted per attempt is no key at all. So the key is held
// against the details being sent and released only once the save has actually landed. Press Save,
// lose signal, press Save again, and the second attempt arrives wearing the first one's name.
//
// This matters most for loans, where a duplicate is not one wrong row but a loan and the cash leg
// that came with it.

/**
 * Posts with a request key that persists across failures.
 *
 * Keyed by the request itself, so two genuinely different saves never share a key, and the same
 * save retried always does. The entry is dropped on success, which is what lets a later identical
 * entry — lending the same amount to the same person again — be its own loan rather than a replay.
 */
export function useIdempotentSave() {
  const pending = useRef(new Map<string, string>());
  return async function save<T>(path: string, body: object): Promise<T> {
    const key = JSON.stringify([path, body]);
    const requestId = pending.current.get(key) ?? crypto.randomUUID();
    pending.current.set(key, requestId);
    const result = await api<T>(path, { ...body, requestId });
    pending.current.delete(key);
    return result;
  };
}
